import type { RequiredStockLevelEntry, RequiredStockLevelSummary } from "#/types/report";
import { Button, Table, Tooltip } from "@mantine/core";
import { IconChartHistogram } from "@tabler/icons-react";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import ReportSection from "./ReportSection";
import StockSimulationModal from "./StockSimulationModal";

interface RequiredStockLevelSectionProps {
  data: RequiredStockLevelSummary;
  tooltip?: ReactNode;
}

const RequiredStockLevelSection = ({ data, tooltip }: RequiredStockLevelSectionProps) => {
  const { t } = useTranslation();
  const [simulatedEntry, setSimulatedEntry] = useState<RequiredStockLevelEntry | null>(null);

  const renderExcess = (excess: number | null) => {
    if (excess === null) return <span className="text-gray-400">--</span>;
    if (excess > 0) {
      return (
        <span className="text-emerald-600">
          +{excess} {t("reports.inventoryScan.requiredStock.idle")}
        </span>
      );
    }
    if (excess < 0) {
      return (
        <span className="text-red-600">
          {excess} {t("reports.inventoryScan.requiredStock.short")}
        </span>
      );
    }
    return <span className="text-gray-600">0</span>;
  };

  return (
    <ReportSection
      title={t("reports.inventoryScan.requiredStock.title")}
      subtitle={
        data.timeDeltaMonths !== null
          ? t("reports.inventoryScan.requiredStock.subtitle", {
              months: data.timeDeltaMonths,
            })
          : t("reports.inventoryScan.requiredStock.noScans")
      }
      tooltip={tooltip}
    >
      {data.byCategory.length > 0 ? (
        <Table striped highlightOnHover withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t("reports.inventoryScan.requiredStock.category")}</Table.Th>
              <Table.Th className="text-right">
                {t("reports.inventoryScan.requiredStock.externalItems")}
              </Table.Th>
              <Table.Th className="text-right">
                {t("reports.inventoryScan.requiredStock.required")}
              </Table.Th>
              <Table.Th className="text-right">
                {t("reports.inventoryScan.requiredStock.current")}
              </Table.Th>
              <Table.Th className="text-right">
                {t("reports.inventoryScan.requiredStock.excess")}
              </Table.Th>
              <Table.Th className="text-right">
                {t("reports.inventoryScan.requiredStock.actions")}
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {data.byCategory.map((entry) => (
              <Table.Tr key={entry.productId ?? entry.productName ?? "unknown"}>
                <Table.Td className="font-medium">
                  {entry.productName ?? t("reports.inventoryScan.requiredStock.noCategory")}
                </Table.Td>
                <Table.Td className="text-right">{entry.externalCount}</Table.Td>
                <Table.Td className="text-right">
                  {entry.requiredStock ?? <span className="text-gray-400">--</span>}
                </Table.Td>
                <Table.Td className="text-right">{entry.currentCount}</Table.Td>
                <Table.Td className="text-right">{renderExcess(entry.excess)}</Table.Td>
                <Table.Td className="text-right">
                  <Tooltip
                    label={t("reports.inventoryScan.simulation.unavailable")}
                    disabled={entry.requiredStock !== null}
                    withArrow
                  >
                    {/* Wrapper keeps the tooltip working while the button is disabled. */}
                    <span className="inline-block">
                      <Button
                        size="compact-xs"
                        variant="outline"
                        color="black"
                        leftSection={<IconChartHistogram size={14} />}
                        disabled={entry.requiredStock === null}
                        onClick={() => setSimulatedEntry(entry)}
                      >
                        {t("reports.inventoryScan.simulation.run")}
                      </Button>
                    </span>
                  </Tooltip>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      ) : (
        <span className="text-sm text-gray-500">
          {t("reports.inventoryScan.requiredStock.noItems")}
        </span>
      )}

      <StockSimulationModal
        opened={simulatedEntry !== null}
        onClose={() => setSimulatedEntry(null)}
        entry={simulatedEntry}
      />
    </ReportSection>
  );
};

export default RequiredStockLevelSection;
